import { Box, ButtonText, CloseIcon, Divider, Heading, HStack, Icon, Input, InputField, Modal, ModalBackdrop, ModalBody, ModalCloseButton, ModalContent, ModalHeader, Text, View, VStack } from '@gluestack-ui/themed';
import { FC, useContext } from 'react';
import { useState } from 'react';
import { Button, Pressable, StyleSheet, TouchableOpacity } from 'react-native';
import { ArrowLeftIcon, Building2, GraduationCap, CheckIcon } from 'lucide-react-native';
import { useFormik } from 'formik';
import { COLORS } from '../theme/colors';
import BackgroundSVG from '../assets/background.svg';
import { Image } from '@gluestack-ui/themed';
import { PrimaryButton } from '../components/buttons/PrimaryButton';
import Bioeureka from '../assets/bioeureka-black.svg';
import { AuthContext } from '../contexts/session/Auth';
import * as Yup from 'yup';
import { useNavigation } from '@react-navigation/native';
import { TranslationContext } from '../contexts/translations/Translation';
import ForgotPasswordModal from '../components/login/ForgotPasswordModal';
import { Routes } from '../routes/Routes';

type Door = 'academic' | 'company';

export const LogInPage: FC<ILoginPageProps> = (_: ILoginPageProps) => {
  const { testID } = _;
  const { i18n, changeLanguage } = useContext(TranslationContext);
  const isFr = (i18n?.locale ?? 'en').startsWith('fr');

  const auth = useContext(AuthContext);
  const navigation = useNavigation<any>();
  const [credentialsError, setCredentialsError] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [feedback, setFeedback] = useState("")
  // Two entry points over the same Auth0 backend (academia vs companies).
  const [door, setDoor] = useState<Door | null>(null);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email(i18n?.t('page.login.invalid_email'))
      .required(i18n?.t('page.login.required_email')),
    password: Yup.string()
      .min(8, i18n?.t('page.login.invalid_password'))
      .required(i18n?.t('page.login.required_password'))
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      password: "",
    },
    validateOnChange: false,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setCredentialsError(false);
      try {
        await auth?.logIn(values.email, values.password);
      } catch (error) {
        setCredentialsError(true)
      }
    },
  });

  const DoorCard = ({ kind, icon, iconBg, iconColor, ctaBg }: { kind: Door; icon: JSX.Element; iconBg: string; iconColor: string; ctaBg: string }) => (
    <Pressable
      testID={`${testID ?? ''}${LogInPage.name}_door_${kind}`}
      onPress={() => setDoor(kind)}
      style={{ flex: 1, minWidth: 230, backgroundColor: COLORS.NEUTRAL[0], borderWidth: 1, borderColor: COLORS.NEUTRAL[70], borderRadius: 14, padding: 20 }}
    >
      <Box style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: iconBg, alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
        {icon}
      </Box>
      <Text fontSize={17} fontWeight="bold">{i18n?.t(`page.login.door-${kind}-title`)}</Text>
      <Text fontSize={13} color={COLORS.NEUTRAL[90]} style={{ marginTop: 2, marginBottom: 12 }}>{i18n?.t(`page.login.door-${kind}-subtitle`)}</Text>
      <HStack gap={6} alignItems="center">
        <CheckIcon size={14} color={iconColor} />
        <Text fontSize={12} color={iconColor}>{i18n?.t(`page.login.door-${kind}-bullet`)}</Text>
      </HStack>
      <Box style={{ marginTop: 16, backgroundColor: ctaBg, borderRadius: 10, paddingVertical: 10, alignItems: 'center' }}>
        <Text fontSize={14} fontWeight="bold" color={COLORS.NEUTRAL[0]}>{i18n?.t('page.login.continue')}</Text>
      </Box>
    </Pressable>
  );

  return (
    <Box style={styles.layout}>
      <Image testID={`${testID ?? ''}${LogInPage.name}_backgroundImage`} alt="" style={styles.backgroundImage} source={{ uri: BackgroundSVG }} />

      <Pressable
        testID={`${testID ?? ''}${LogInPage.name}_langToggle`}
        onPress={() => changeLanguage(isFr ? 'en' : 'fr')}
        style={{ position: 'absolute', top: 20, right: 24, borderWidth: 1, borderColor: COLORS.NEUTRAL[70], borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6, backgroundColor: COLORS.NEUTRAL[0] }}
      >
        <Text fontSize={13} fontWeight="bold" color={COLORS.TERCIARY[100]}>{isFr ? 'EN' : 'FR'}</Text>
      </Pressable>

      {door === null ? (
        <VStack style={{ width: '100%', maxWidth: 560, alignItems: 'center' }}>
          <Image testID={`${testID ?? ''}${LogInPage.name}_logo`} alt="Logo" source={{ uri: Bioeureka }} height={48} width={300} style={{ marginBottom: 6 }} />
          <Text fontSize={14} color={COLORS.NEUTRAL[90]} style={{ marginBottom: 22 }}>{i18n?.t('page.login.choose-access')}</Text>
          <HStack gap={16} flexWrap="wrap" style={{ width: '100%' }}>
            <DoorCard
              kind="academic"
              iconBg="#E1F5EE"
              iconColor="#0F6E56"
              ctaBg={COLORS.ACCENT[100]}
              icon={<GraduationCap size={22} color="#0F6E56" />}
            />
            <DoorCard
              kind="company"
              iconBg="#E7EDF2"
              iconColor={COLORS.TERCIARY[100]}
              ctaBg={COLORS.TERCIARY[100]}
              icon={<Building2 size={22} color={COLORS.TERCIARY[100]} />}
            />
          </HStack>
        </VStack>
      ) : (
        <VStack style={{ width: '100%', maxWidth: 440 }}>
          <Pressable testID={`${testID ?? ''}${LogInPage.name}_changeAccess`} onPress={() => setDoor(null)} style={{ marginBottom: 14 }}>
            <HStack gap={6} alignItems="center">
              <ArrowLeftIcon size={16} color={COLORS.ACCENT[100]} />
              <Text fontSize={13} color={COLORS.ACCENT[100]}>{i18n?.t('page.login.change-access')}</Text>
            </HStack>
          </Pressable>

          <Box
            backgroundColor={COLORS.NEUTRAL[0]}
            paddingVertical={30}
            paddingHorizontal={50}
            borderRadius={20}
            shadowColor='#171717'
            shadowOffset={{ width: -2, height: 4 }}
            shadowOpacity={0.2}
            shadowRadius={3}
          >
            <Image testID={`${testID ?? ''}${LogInPage.name}_logo`} alt="Logo" source={{ uri: Bioeureka }} height={48} width={340} />
            <Text fontSize={13} color={COLORS.NEUTRAL[90]} style={{ marginTop: 6 }}>
              {i18n?.t(door === 'academic' ? 'page.login.access-academic' : 'page.login.access-company')}
            </Text>
            <Divider marginVertical={20} />

            <Input marginTop={20} marginBottom={5}>
              <InputField
                testID={`${testID ?? ''}${LogInPage.name}_emailInput`}
                onChangeText={formik.handleChange('email')}
                onBlur={formik.handleBlur('email')}
                value={formik.values.email}
                placeholder={i18n?.t('page.login.email')}
                keyboardType="email-address"
                autoCapitalize="none"
                onFocus={() => {
                  formik.setErrors({});
                }}
              />
            </Input>
            {formik.errors.email && (
              <Text testID={`${testID ?? ''}${LogInPage.name}_emailInputError`} color={COLORS.STATUS.WARNING_2}>{formik.errors.email}</Text>
            )}

            <Input marginTop={20} marginBottom={5}>
              <InputField
                testID={`${testID ?? ''}${LogInPage.name}_passwordInput`}
                onChangeText={formik.handleChange('password')}
                onBlur={formik.handleBlur('password')}
                value={formik.values.password}
                placeholder={i18n?.t('page.login.password')}
                secureTextEntry
              />
            </Input>
            {formik.errors.password && (
              <Text testID={`${testID ?? ''}${LogInPage.name}_passwordInputError`} marginBottom={10} color={COLORS.STATUS.WARNING_2}>{formik.errors.password}</Text>
            )}

            <Pressable onPress={()=>{setShowModal(true)}}>
              <Text color="blue" marginTop={20}>{i18n?.t('page.login.forgot_password')}</Text>
            </Pressable>
            <PrimaryButton
              testID={`${testID ?? ''}${LogInPage.name}_loginButton`}
              style={{marginTop: 35, alignSelf: 'center'}}
              isLoading={auth?.isLoading}
              content={i18n?.t('page.login.login')}
              onPress={async ()=> { formik.handleSubmit() }}/>

            <Pressable
              testID={`${testID ?? ''}${LogInPage.name}_secondaryCta`}
              onPress={() => { if (door === 'academic') navigation.navigate(Routes.REGISTER); }}
              style={{ marginTop: 18 }}
            >
              <Text fontSize={13} color={COLORS.ACCENT[100]} style={{ textAlign: 'center' }}>
                {i18n?.t(door === 'academic' ? 'page.login.academic-register' : 'page.login.company-contact')}
              </Text>
            </Pressable>
          </Box>
        </VStack>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
        }}
      >
        <ModalBackdrop />
        <ModalContent>
          <ForgotPasswordModal setFeedback={setFeedback} setShowModal={setShowModal} auth={auth} i18n={i18n}/>
        </ModalContent>
      </Modal>
      {feedback && <Text color={COLORS.STATUS.SUCCESS} margin={20}>{feedback}</Text>}
      {credentialsError && <Text testID={`${testID ?? ''}${LogInPage.name}_loginErrorText`} color={COLORS.STATUS.WARNING_2} margin={20}>{i18n?.t('page.login.error')}</Text>}
    </Box>
  );
};

const styles = StyleSheet.create({
  layout: {
    backgroundColor: COLORS.FOUNDATION_PRIMARY,
    height: '100%',
    paddingHorizontal: '10%',
    flexDirection: 'column',
    display: 'flex',
    overflow: 'scroll',
    alignItems: 'center',
    justifyContent: 'center'
  },
  backgroundImage: {
    position: "absolute",
    width: 450,
    height: 450,
    top: 0,
    right: -60,
    zIndex: -1,
  }
});
